import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

const humanPoints = [
    { text: 'Analysts must manually review and validate alerts before acting', note: 'Minutes to hours of delay per incident' },
    { text: 'Alert fatigue causes real threats to be missed or deprioritized', note: 'SIEM tools average thousands of alerts per day' },
    { text: 'Coverage gaps during nights, weekends, and holidays', note: '24/7 protection requires 3+ full-time staff' },
    { text: 'Inconsistency and human error in threat assessment', note: 'Different analysts reach different conclusions' },
    { text: 'Cannot scale efficiently across many tenants or users', note: 'Cost grows linearly with headcount' },
];

const aiPoints = [
    { text: 'Behavioral baselines detect threats the moment they deviate', note: 'Sub-minute detection — no human in the loop' },
    { text: 'Adaptive learning reduces false positives over time', note: 'Alerts are actionable, not noise' },
    { text: 'Continuous 24/7 monitoring with no gaps', note: 'No shift changes, holidays, or sick days' },
    { text: 'Consistent, objective threat scoring every time', note: 'Same rigor applied to every event, every user' },
    { text: 'Scales across thousands of users and tenants instantly', note: 'Cost stays flat as your environment grows' },
];

export default function RemoveHumans() {
    return (
        <Box
            width="100%"
            sx={{
                background: '#ffffff',
                display: 'grid',
                placeItems: 'center',
                py: 10,
            }}
        >
            <Grid container maxWidth="lg" spacing={2} justifyContent="center" sx={{ px: 2 }}>
                <Grid item xs={12} textAlign="center" mb={4}>
                    <Typography
                        variant="overline"
                        color="prevailRed.main"
                        fontWeight="bold"
                        sx={{ letterSpacing: 3, fontSize: '0.9em' }}
                    >
                        WHY AI WINS
                    </Typography>
                    <Typography variant="h3" color="grey.800" fontWeight="bold" mt={1}>
                        Removing the Human Bottleneck
                    </Typography>
                    <Typography variant="h6" color="grey.600" mt={2} sx={{ maxWidth: '680px', mx: 'auto' }}>
                        Managed threat detection that relies on humans to review and validate alerts
                        is only as fast as the slowest person on shift. PREVAIL removes that bottleneck entirely.
                    </Typography>
                </Grid>

                {/* Human side */}
                <Grid item size={{ xs:12, md: 5}}>
                    <Box
                        sx={{
                            border: '2px solid #e0e0e0',
                            borderRadius: 2,
                            p: 4,
                            height: '100%',
                        }}
                    >
                        <Typography variant="h5" color="grey.700" fontWeight="bold" mb={3}>
                            Human-Dependent Detection
                        </Typography>
                        {humanPoints.map(({ text, note }) => (
                            <Box key={text} mb={2.5}>
                                <Box display="flex" alignItems="flex-start" gap={1.5}>
                                    <CancelIcon sx={{ color: '#d90000', fontSize: 20, flexShrink: 0, mt: 0.2 }} />
                                    <Box>
                                        <Typography variant="body2" color="grey.700" sx={{ fontSize: '0.97em' }}>
                                            {text}
                                        </Typography>
                                        <Typography variant="caption" color="grey.400" sx={{ fontStyle: 'italic' }}>
                                            {note}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Grid>

                {/* Divider */}
                <Grid item size={{ xs:0, md: 1}} sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', justifyContent: 'center' }}>
                    <Divider orientation="vertical" flexItem sx={{ borderColor: 'grey.300', mx: 'auto' }} />
                </Grid>

                {/* AI side */}
                <Grid item size={{ xs: 12, md: 5}}>
                    <Box
                        sx={{
                            border: '2px solid #d90000',
                            borderRadius: 2,
                            p: 4,
                            height: '100%',
                            boxShadow: '0 4px 20px rgba(217,0,0,0.08)',
                        }}
                    >
                        <Typography variant="h5" color="prevailRed.main" fontWeight="bold" mb={3}>
                            PREVAIL Automated Detection
                        </Typography>
                        {aiPoints.map(({ text, note }) => (
                            <Box key={text} mb={2.5}>
                                <Box display="flex" alignItems="flex-start" gap={1.5}>
                                    <CheckCircleIcon sx={{ color: '#47A025', fontSize: 20, flexShrink: 0, mt: 0.2 }} />
                                    <Box>
                                        <Typography variant="body2" color="grey.700" sx={{ fontSize: '0.97em' }}>
                                            {text}
                                        </Typography>
                                        <Typography variant="caption" color="grey.500" sx={{ fontStyle: 'italic' }}>
                                            {note}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Grid>

                <Grid item xs={12} textAlign="center" mt={2}>
                    <Typography variant="body2" color="grey.500" sx={{ fontStyle: 'italic' }}>
                        Organizations using AI and automation identify and contain breaches 108 days faster on average.
                        &nbsp;&nbsp;—&nbsp;&nbsp;IBM Cost of a Data Breach 2023
                    </Typography>
                </Grid>
            </Grid>
        </Box>
    );
}
